import React, { useContext, useEffect, useState } from 'react';
import './ObjectView.scss';
import { IconButton, Portal } from '@material-ui/core';

import { ViewClientT } from 'bovaljaren-models';
import { useObserver } from 'mobx-react-lite';
import { ObosX } from '../../../../icons/obos/obosIcons';
import BrandingService from '../../../../services/branding.service';
import { AppContext } from '../../../../state/app.state';
import PolygonPreview from '../../../image/PolygonPreview/PolygonPreview';
import MailToButton from '../../../interaction/buttons/MailToButton/MailToButton';
import ObjectViewDocument from './ObjectViewDocument/ObjectViewDocument';
import ObjectViewIframes from './ObjectViewIframes/ObjectViewIframes';
import ObjectViewVideoPlayer from './ObjectViewVideoPlayer/ObjectViewVideoPlayer';
import ObjectViewImageGallery from './ObjectViewImageGallery/ObjectViewImageGallery';
import ObjectViewButtons from './ObjectViewButtons/ObjectViewButtons';
import { ObjectViewPaper, ObjectViewLeft, LeftSubCol, LeftSubColContent, ObjectViewRight, ObjectViewLeftLower, ObjectViewContent, ObjectViewWrapper, CurrentImageT } from './OV-subcomponents';
import ObjectViewFacts from './ObjectViewFacts/ObjectViewFacts';
import ObjectViewFloorplanImage from './ObjectViewFloorplanImage/ObjectViewFloorplanImage';
import ObjectViewDescription from './ObjectViewDescription/ObjectViewDescription';
import ObjectViewInterestForm from './ObjectViewInterestForm/ObjectViewInterestForm';

type Props={
    fullscreen: boolean;
    portal: boolean;
    show: boolean;
    onClose: () => void;
}
export type ToggleValuesT = 'documents' | 'images' | 'iframe1' | 'video' | 'iframe2' | 'interestform' | 'description';

const ObjectView: React.FC<Props> = ({ fullscreen, portal, onClose, show }) => {
    const { state } = useContext(AppContext);
    const statuses = state?.project?.statuses;
    let selectedMapping = state?.currentView?.mappings.objects?.find(mO => mO.mappingEntityId === state?.selectedObjectId);
    let externalView: ViewClientT | undefined;
    const [showIframeOne, setShowIframeOne] = useState(false);
    const [showIframeTwo, setShowIframeTwo] = useState(false);
    const [showVideo, setShowVideo] = useState(false);
    const [showImages, setShowImages] = useState(true);
    const [showDocuments, setShowDocuments] = useState(false);
    const [showDescription, setShowDescription] = useState(false);
    const [showInterestForm, setShowInterestForm] = useState(false);

    if (!selectedMapping) {
        externalView = state?.project?.views.find(v => v.mappings.objects.find(mO => mO.mappingEntityId === state.selectedObjectId));
        selectedMapping = externalView?.mappings.objects.find(mO => mO.mappingEntityId === state?.selectedObjectId);
    }

    const toggleShow = (value: ToggleValuesT) => {
        setShowDocuments(false);
        setShowImages(false);
        setShowIframeOne(false);
        setShowIframeTwo(false);
        setShowVideo(false);
        setShowDescription(false);
        setShowInterestForm(false);

        switch (value) {
        case 'documents':
            setShowDocuments(true);
            break;
        case 'images':
            setShowImages(true);
            break;
        case 'iframe1':
            setShowIframeOne(true);
            break;
        case 'iframe2':
            setShowIframeTwo(true);
            break;
        case 'video':
            setShowVideo(true);
            break;
        case 'interestform':
            setShowInterestForm(true);
            break;
        case 'description':
            setShowDescription(true);
            break;
        default:
        }
    };

    const selectedObject = state?.objects?.find(o => o.id === state.selectedObjectId);
    const fileCollections = state?.fileCollections;
    const types = state?.project?.objectTypes;
    const viewImage = externalView ? externalView.imageFile : state?.currentView?.imageFile;


    const [currentImages, setCurrentImages] = useState<CurrentImageT[] | undefined>();
    const [imageIndex, setImageIndex] = useState(0);
    const [prevImageIndex, setPrevImageIndex] = useState(-1);

    const currentImage = currentImages?.find((f, i) => i === imageIndex);


    useEffect(() => {
        setImageIndex(0);
        setPrevImageIndex(-1);
        toggleShow('images');
        setShowDescription(false);

        const imageCollectionImages = selectedObject && fileCollections?.find(fc => {
            return fc.id === selectedObject.imageCollectionId;
        })
            ?.files?.slice().sort((a, b) => {
                return a.sortOrder - b.sortOrder;
            })
            ?.filter(f => f.type === 'image')
            .map(f => ({ image: f, subtype: 'image' }));

        const floorplanCollectionItems = selectedObject && fileCollections?.find(fc => {
            return fc.id === selectedObject.floorPlanCollectionId;
        })
            ?.files?.slice().sort((a, b) => {
                return a.sortOrder - b.sortOrder;
            })
            ?.filter(f => f.type === 'image')
            .map(f => ({ image: f, subtype: 'floorplan' }));


        if (imageCollectionImages && floorplanCollectionItems) {
            setCurrentImages(imageCollectionImages.concat(floorplanCollectionItems));
        }
        else if (imageCollectionImages) {
            setCurrentImages(imageCollectionImages);
        }
        else if (floorplanCollectionItems)
            setCurrentImages(floorplanCollectionItems);


    }, [fileCollections, selectedObject]);


    useEffect(() => {
        setTimeout(function sendSize () {
            if (!state?.pymContextFrame) return;

            const currentHeight = document.getElementById('bv-root-element')?.clientHeight;

            currentHeight && state?.pymContextFrame?.sendHeight && state.pymContextFrame.sendHeight(currentHeight);
        }, 200);
    });
    const oT = state?.project?.objectTypes.find(oT => oT.id === selectedObject?.objectTypeId)?.name;
    const title = oT ? `${oT} ${selectedObject?.name || ''}` : `L�genhet ${ selectedObject?.name || ''}`;

    return useObserver(() => <>{ show && <Portal disablePortal={!portal}>
        <ObjectViewWrapper fullscreen={fullscreen} onClose={onClose}>
            <ObjectViewPaper
                currentImage={currentImage}
                onClose={onClose}
                showIframeOne={showIframeOne}
                selectedObject={selectedObject}
                toggleShow={toggleShow}>
                <IconButton className={'bv-objectview-close'} onClick={(evt): void => {
                    toggleShow('images');
                    onClose && onClose();
                }}><ObosX className={'bv-obos-x'} /></IconButton>
                {selectedObject &&
                    <ObjectViewContent>
                        <ObjectViewLeft selectedObject={selectedObject} showIframeOne={showIframeOne} currentImage={currentImage}>
                            {selectedObject && viewImage && <PolygonPreview
                                statuses={statuses}
                                image={viewImage}
                                object={selectedObject}
                                floorplan={currentImage?.subtype === 'floorplan'}
                                mapping={(state?.project?.views.find(v => v?.mappings.objects?.find(mO => mO.mappingEntityId === state?.selectedObjectId) as unknown as ViewClientT)?.mappings.objects.find(o => o.mappingEntityId === selectedObject.id))}
                            >
                            </PolygonPreview>}
                            <ObjectViewLeftLower title={title}/>
                            <LeftSubCol>
                                <LeftSubColContent>
                                    <ObjectViewFacts selectedObject={selectedObject} types={types}/>
                                </LeftSubColContent>
                                <LeftSubColContent>
                                    <ObjectViewFloorplanImage setImageIndex={setImageIndex} setPrevImageIndex={setPrevImageIndex} currentImages={currentImages} selectedObject={selectedObject} toggleShow={toggleShow} imageIndex={imageIndex}/>
                                </LeftSubColContent>
                            </LeftSubCol>
                            <ObjectViewButtons
                                selectedObject={selectedObject}
                                setShowDescription={setShowDescription}
                                showDescription={showDescription}
                                showIframeOne={showIframeOne}
                                showVideo={showVideo}
                                showIframeTwo={showIframeTwo}
                                showImages={showImages}
                                showDocuments={showDocuments}
                                showInterestForm={showInterestForm}
                                types={types}
                                toggleShow={toggleShow}/>
                        </ObjectViewLeft>
                        <ObjectViewRight selectedObject={selectedObject} showIframeOne={showIframeOne} currentImage={currentImage}>
                            <ObjectViewImageGallery
                                currentImage={currentImage}
                                currentImages={currentImages}
                                setImageIndex={setImageIndex}
                                imageIndex={imageIndex}
                                showImages={showImages}
                                prevImageIndex={prevImageIndex}
                                setPrevImageIndex={setPrevImageIndex}
                                selectedObject={selectedObject}
                            />
                            <ObjectViewIframes
                                showIframeOne={showIframeOne}
                                showIframeTwo={showIframeTwo}
                                selectedObject={selectedObject}
                                toggleShow={toggleShow}
                            />

                            <ObjectViewDescription showDescription={showDescription} selectedObject={selectedObject} types={types}/>
                            <ObjectViewVideoPlayer
                                showVideo={showVideo}
                                selectedObject={selectedObject}
                            />
                            <ObjectViewInterestForm
                                showInterestForm={showInterestForm}
                                selectedObject={selectedObject}
                            />
                            <ObjectViewDocument selectedObject={selectedObject} showDocuments={showDocuments}/>
                        </ObjectViewRight>
                    </ObjectViewContent>}
            </ObjectViewPaper>
        </ObjectViewWrapper>
    </Portal>}</>);
};


export default ObjectView;